import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(e.target as Node)) {
        setMobileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setIsOpen(false);
  }, [location]);

  const closeMobile = () => {
    setMobileOpen(false);
    setIsOpen(false);
  };

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-10">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Left Section */}
          <div className="flex items-center gap-2 md:gap-4">
            {/* Mobile Hamburger */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className={`md:hidden flex items-center justify-center w-10 h-10 rounded-full border cursor-pointer transition-all duration-200 ${
                isScrolled
                  ? 'border-foreground-300/60 text-foreground-800 bg-background-50'
                  : 'border-white/40 text-white'
              }`}
              aria-label="Menu"
            >
              <i className={`text-lg w-5 h-5 flex items-center justify-center ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>

            {/* Home Button */}
            <Link
              to="/"
              className={`hidden md:inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-all duration-200 ${
                isScrolled
                  ? 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                  : 'text-white/80 hover:text-white hover:bg-white/10'
              }`}
            >
              <i className="ri-home-4-line text-base w-4 h-4 flex items-center justify-center"></i>
              <span>Home</span>
            </Link>

            {/* Category Dropdown */}
            <div ref={dropdownRef} className="relative hidden md:block">
              <button
                onClick={() => setIsOpen(!isOpen)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-full border text-sm font-medium whitespace-nowrap cursor-pointer transition-all duration-200 ${
                  isScrolled
                    ? 'border-foreground-300/60 text-foreground-800 hover:border-foreground-500 bg-background-50'
                    : 'border-white/40 text-white hover:border-white/80 hover:bg-white/10'
                }`}
              >
                <i className="ri-menu-line text-base w-4 h-4 flex items-center justify-center"></i>
                <span>Seleziona Categoria</span>
                <i className={`ri-arrow-down-s-line text-sm w-4 h-4 flex items-center justify-center transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {isOpen && (
                <div className="absolute top-full mt-2 left-0 w-64 bg-background-50 border border-background-200/70 rounded-lg py-2 max-h-[420px] overflow-y-auto">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors duration-150 whitespace-nowrap"
                      onClick={() => setIsOpen(false)}
                    >
                      <i className={`${cat.icon} text-base text-primary-500 w-5 h-5 flex items-center justify-center`}></i>
                      <span>{cat.name}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* Guida allo Shopping Button */}
            <Link
              to="/guida-completa"
              className={`hidden md:inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-all duration-200 ${
                isScrolled
                  ? 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                  : 'text-white/80 hover:text-white hover:bg-white/10'
              }`}
            >
              <i className="ri-book-open-line text-base w-4 h-4 flex items-center justify-center"></i>
              <span>Guida allo Shopping</span>
            </Link>
          </div>

          {/* Center: Brand */}
          <div className="hidden md:block">
            <Link
              to="/"
              className={`font-heading text-xl italic tracking-wide whitespace-nowrap transition-colors duration-200 ${
                isScrolled ? 'text-foreground-900' : 'text-white'
              }`}
            >
              Itaobuy Spreadsheet <span className="text-xs font-body not-italic ml-1 opacity-70">Italia</span>
            </Link>
          </div>

          {/* Mobile Brand */}
          <Link
            to="/"
            className={`md:hidden font-heading text-base italic tracking-wide whitespace-nowrap transition-colors duration-200 ${
              isScrolled ? 'text-foreground-900' : 'text-white'
            }`}
          >
            Itaobuy Spreadsheet
          </Link>

          {/* Right: CTA Buttons */}
          <div className="flex items-center gap-2 md:gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className={`hidden sm:inline-flex px-3 md:px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-all duration-200 ${
                isScrolled
                  ? 'text-foreground-700 hover:text-foreground-950'
                  : 'text-white/80 hover:text-white'
              }`}
            >
              Itaobuy Sheet
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className={`px-4 md:px-5 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer transition-all duration-200 ${
                isScrolled
                  ? 'bg-primary-500 text-background-50 hover:bg-primary-600'
                  : 'bg-white text-foreground-900 hover:bg-white/90'
              }`}
            >
              Buy It Now
            </a>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 top-16 bg-foreground-950/40 z-40" onClick={closeMobile}></div>
      )}

      {/* Mobile Menu Panel */}
      <div
        ref={mobileMenuRef}
        className={`md:hidden fixed top-16 left-0 w-full max-h-[calc(100vh-4rem)] bg-background-50 border-b border-background-200/70 overflow-y-auto z-50 transition-all duration-300 ${
          mobileOpen ? 'translate-y-0 opacity-100' : '-translate-y-2 opacity-0 pointer-events-none'
        }`}
      >
        <div className="px-4 py-4 space-y-4">
          {/* Quick Nav Links */}
          <div className="space-y-1">
            <Link
              to="/"
              onClick={closeMobile}
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors duration-150"
            >
              <i className="ri-home-4-line text-lg text-primary-500 w-5 h-5 flex items-center justify-center"></i>
              <span>Home</span>
            </Link>
            <Link
              to="/guida-completa"
              onClick={closeMobile}
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors duration-150"
            >
              <i className="ri-book-open-line text-lg text-primary-500 w-5 h-5 flex items-center justify-center"></i>
              <span>Guida allo Shopping</span>
            </Link>
            <Link
              to="/sicurezza-acquisti"
              onClick={closeMobile}
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors duration-150"
            >
              <i className="ri-shield-check-line text-lg text-accent-600 w-5 h-5 flex items-center justify-center"></i>
              <span>Sicurezza Acquisti</span>
            </Link>
            <Link
              to="/confronto-categorie"
              onClick={closeMobile}
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors duration-150"
            >
              <i className="ri-bar-chart-line text-lg text-accent-600 w-5 h-5 flex items-center justify-center"></i>
              <span>Confronto Categorie</span>
            </Link>
          </div>

          {/* Divider */}
          <div className="border-t border-background-200/70"></div>

          {/* Category Label */}
          <p className="px-4 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Categorie Prodotto</p>

          {/* Categories */}
          <div className="space-y-1">
            {categories.map((cat) => (
              <a
                key={cat.name}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                onClick={closeMobile}
                className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors duration-150"
              >
                <i className={`${cat.icon} text-base text-primary-500 w-5 h-5 flex items-center justify-center`}></i>
                <span>{cat.name}</span>
                <span className="ml-auto text-xs text-foreground-400">{cat.count}</span>
              </a>
            ))}
          </div>

          {/* Divider */}
          <div className="border-t border-background-200/70"></div>

          {/* External Links */}
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            onClick={closeMobile}
            className="flex items-center justify-center gap-2 w-full py-3 rounded-full bg-foreground-950 text-background-50 text-sm font-semibold whitespace-nowrap cursor-pointer transition-all duration-200"
          >
            <i className="ri-external-link-line text-base"></i>
            Vai al Sito Itaobuy Sheet
          </a>
        </div>
      </div>
    </nav>
  );
}